import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RoleEnum } from '@/types/enums/role.enum';

interface UserInformation {
    id: string;
    firstname: string;
    lastname: string;
    fullname: string;
    email: string;
    phoneNumber: string;
    position: string;
    username: string;
    roles: any;
    isCustomer: boolean;
    customerId: string | null;
    clientRole?: string;
}

interface AuthUser {
    id: string;
    name: string;
    role: string;
    permissions: string[];
}

interface AuthState {
    user: AuthUser | null;
    userInformation: UserInformation;
    status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated';
    error: string | null;
    expiresAt: number | null;
}

const initialState: AuthState = {
    user: null,
    userInformation: {} as UserInformation,
    status: 'idle',
    error: null,
    expiresAt: null,
};

export const loginThunk = createAsyncThunk(
    'auth/login',
    async (
        creds: { userName: string; password: string },
        { rejectWithValue, dispatch }
    ) => {
        const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(creds),
        });

        const data = await res.json().catch();

        if (!res.ok) return rejectWithValue(data.error || 'Login failed');

        try {
            const resp = await fetch('/api/users', { method: 'GET' });

            const result = await resp.json();

            const info = result.result;

            dispatch(setUserInformation(info));
        } catch (err: any) {
            console.error('Lấy user info lỗi:', err);
        }

        return data as { user: AuthUser; expiresAt: number };
    }
);

export const refreshThunk = createAsyncThunk(
    'auth/refresh',
    async (_, { rejectWithValue }) => {
        const res = await fetch('/api/auth/refresh', { method: 'POST' });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) return rejectWithValue(data.error || 'Refresh failed');

        return data as { expiresAt: number };
    }
);

export const logoutThunk = createAsyncThunk(
    'auth/logout',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetch('/api/auth/logout', { method: 'POST' });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));

                return rejectWithValue(errorData.message || 'Logout failed');
            }

            return true;
        } catch (error) {
            return rejectWithValue(
                'Logout failed due to network or server error'
            );
        }
    }
);

export const bootstrapAuthThunk = createAsyncThunk(
    'auth/bootstrap',
    async (_, { rejectWithValue }) => {
        const res = await fetch('/api/Auth/user-login-permission', {
            method: 'POST',
        });

        if (res.status === 401) {
            return rejectWithValue('unauthenticated');
        }

        const data = await res.json();

        return data.result;
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setUserInformation(state, { payload }) {
            state.userInformation = payload ?? ({} as UserInformation);

            const roles = Array.isArray(payload?.appRoles)
                ? payload.appRoles
                : [];

            // default
            state.userInformation.clientRole = 'Default';

            for (const role of roles) {
                const r = String(role?.role ?? '').toLowerCase();

                if (r === RoleEnum.ADMIN.toLowerCase()) {
                    state.userInformation.clientRole = RoleEnum.ADMIN;
                    break;
                }

                if (r === RoleEnum.CUSTOMER.toLowerCase()) {
                    state.userInformation.clientRole = RoleEnum.CUSTOMER;
                    break;
                }

                if (r === RoleEnum.KTV.toLowerCase()) {
                    state.userInformation.clientRole = RoleEnum.KTV;
                    break;
                }
            }
        },

        setAuthError(state, { payload }) {
            state.error = payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(loginThunk.pending, state => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(loginThunk.fulfilled, (state, { payload }) => {
                state.status = 'idle';
                state.user = payload.user;
                state.expiresAt = payload.expiresAt;
            })
            .addCase(loginThunk.rejected, (state, action) => {
                state.status = 'unauthenticated';
                state.user = null;
                state.error = action.payload as string;
            })
            .addCase(refreshThunk.fulfilled, (state, { payload }) => {
                state.expiresAt = payload.expiresAt;
            })
            .addCase(refreshThunk.rejected, state => {
                state.status = 'unauthenticated';
                state.user = null;
                state.expiresAt = null;
            })
            .addCase(logoutThunk.pending, state => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(logoutThunk.fulfilled, state => {
                state.status = 'unauthenticated';
                state.user = null;
                state.expiresAt = null;
            })
            .addCase(logoutThunk.rejected, state => {
                state.status = 'authenticated';
                state.error = 'Logout failed';
            })
            .addCase(bootstrapAuthThunk.pending, state => {
                state.status = 'loading';
            })
            .addCase(bootstrapAuthThunk.fulfilled, (state, { payload }) => {
                state.status = 'authenticated';

                if (state.user) {
                    state.user.permissions = payload;
                }

                if (payload.expiresAt) {
                    state.expiresAt = payload.expiresAt;
                }
            })
            .addCase(bootstrapAuthThunk.rejected, state => {
                state.status = 'unauthenticated';
                state.user = null;
                state.expiresAt = null;
            });
    },
});

export const { setUserInformation, setAuthError } = authSlice.actions;
export default authSlice.reducer;
